import {TesseractDeviceOverviewContext} from "./context/TesseractDeviceOverviewContext";
import {GlobalStateContext} from "../../global/context/GlobalStateContext";
import React, {useContext, useEffect, useRef, useState} from "react";
import {DraftContext} from "../../draftSave/context/DraftSaveContext";
import {ASCENDING, CONFIG_TYPE, OPERATION_COLOR, SEARCH_ROW_COLOR} from "../../util/Constants";
import {injectIntl} from "react-intl";
import {
    chainWalk,
    getAssetRefreshInterval,
    getTestConfigKey,
    sortComparator
} from "../../util/Util";
import {getLocalizedMessage} from "../../util/Localization";
import {TableCell, TableRow} from "@amzn/meridian/table";
import Input from "@amzn/meridian/input";
import arrowRightTokens from "@amzn/meridian-tokens/base/icon/arrow-right-large";
import Icon from "@amzn/meridian/icon";
import {getAssetByVersion, getAssetVersions} from "../../resources/AssetResource";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import RollbackModal from "../../rollback/RollbackModal";
import ConfigurationTabHeader from "../../commonui/ConfigurationTabHeader";
import PaginatedTable from "../../commonui/PaginatedTable";
import plusCircleTokens from "@amzn/meridian-tokens/base/icon/plus-circle";
import Button from "@amzn/meridian/button";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import AddTesseractTestPackageModal from "./modal/AddTesseractTestPackageModal";
import uploadTokens from "@amzn/meridian-tokens/base/icon/upload-small";
import Box from "@amzn/meridian/box";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import UploadFileModal from "../../commonui/UploadFileModal";
import {TesseractStowConfigCSVParser, TesseractTestConfigCSVParser} from "../../util/Parsers";
import DeleteTestPackageConfigurationModal from "../../commonui/DeleteTestPackageConfigurationModal";
import AddTesseractStowConfigurationModal from "./modal/AddTesseractStowConfigurationModal";
import DeleteTesseractStowConfigurationModal from "./modal/DeleteTesseractStowConfigurationModal";

const TesseractDeviceConfiguration = ({intl}) => {

    //Add Context
    const {viewModel, dispatch} = useContext(TesseractDeviceOverviewContext);
    const {getDraftData, setDraftData, resetDraftData, getFilteredDraftData, isDataEdited} = useContext(DraftContext);
    const { showCustomAlert } = useContext(GlobalStateContext);

    const [selectedMac, setSelectedMac] = useState("");
    const [valueConfiguration, setValueConfiguration] = useState("");
    const [rollBackVersions, setRollbackVersions] = useState([]);
    const [isVersionsLoading, setIsVersionsLoading] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState("");

    // Modal state
    const [showRollbackModal, setShowRollbackModal] = useState(false)
    const [openAddModal, setOpenAddModal] = useState(false)
    const [openDeleteModal, setOpenDeleteModal] = useState(false)
    const [openCSVModal, setOpenCSVModal] = useState(false)

    // Search row state values
    const [scannablesSearchInput, setScannablesSearchInput] = useState('');
    const [destinationSearchInput, setDestinationSearchInput] = useState('');
    //Data Sort prefs for test config table
    const [testConfigSortColumn, setTestConfigSortColumn] = useState("scannables");
    const [testConfigSortOrder, setTestConfigSortOrder] = useState(ASCENDING);
    const [stowConfigSortColumn, setStowConfigSortColumn] = useState("containerType");
    const [stowConfigSortOrder, setStowConfigSortOrder] = useState(ASCENDING);

    const tableEndRef = useRef(null);

    const PAGE_CONFIGURATION_KEYS = [CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION, CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION];

    useEffect(() => {
        if (!openAddModal && !openDeleteModal) {
            getData()
            const interval = setInterval(() => {
                if (!document.hidden) {
                    getData();
                }
            }, getAssetRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [openAddModal, openDeleteModal]);

    useEffect(() => {
        setDraftData({
            data: chainWalk(() => viewModel.stowConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION,
            subKey: selectedMac,
        })
        setDraftData({
            data: chainWalk(() => viewModel.testConfig[selectedMac], []),
            stateKey: CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION,
            subKey: selectedMac,
        })
    }, [selectedMac]);

    const scrollToBottom = () => {
        tableEndRef.current.scrollIntoView({behavior: "smooth"})
    }

    const getData = () => {
        if(isDataEdited(PAGE_CONFIGURATION_KEYS)) return;
        setOpenAddModal(false)
        setOpenDeleteModal(false)
        dispatch({type: 'LOAD_VIEW', data: {dispatcher: dispatch}});
    }

    const getModalContent = async () => {
        setIsVersionsLoading(true);
        let versionsData = await getAssetVersions(selectedMac);
        setRollbackVersions(versionsData.data.assetVersionDetailsList);
        setIsVersionsLoading(false);
    }

    const loadRollbackDataToDraft = async () => {
        let rollbackData = await getAssetByVersion(selectedMac, selectedVersion);
        const testConfigData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes.testConfig), []);
        const stowConfigData = chainWalk(() => JSON.parse(rollbackData.data.asset.assetDetails.assetAttributes.stowConfig), []);
        if(testConfigData.length) {
            setDraftData({
                data: testConfigData,
                stateKey: CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
        if(stowConfigData.length) {
            setDraftData({
                data: stowConfigData,
                stateKey: CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION,
                subKey: selectedMac,
                allRowsAdded: true,
                override: true
            });
        }
    }

    const updateTestConfigDraftData = (stowTestPackage) => {
        setDraftData({
            data: chainWalk(() => stowTestPackage, []),
            stateKey: CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const updateStowConfigDraftData = (stowConfig) => {
        setDraftData({
            data: chainWalk(() => stowConfig, []),
            stateKey: CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION,
            subKey: selectedMac,
            allRowsAdded: true,
            override: true
        })
    }

    const deployDraftTesseractAsset = () => {
        const testPackageList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION,
            subKey: selectedMac
        })

        const stowConfigList = getFilteredDraftData({
            stateKey: CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION,
            subKey: selectedMac
        })

        dispatch({
            type: 'UPDATE_TESSERACT_CONFIGURATION',
            data: {
                dispatcher: dispatch,
                showCustomAlert: showCustomAlert,
                configurations: {
                    testConfig: testPackageList,
                    stowConfig: stowConfigList
                },
                mac: selectedMac
            }
        });

        setDraftData({
            data: testPackageList,
            stateKey: CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })

        setDraftData({
            data: stowConfigList,
            stateKey: CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION,
            subKey: selectedMac,
            override: true
        })
    }

    const searchDataForTestConfigTable = () => {
        let data = getDraftData({
            stateKey: CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION,
            subKey: selectedMac
        })

        data = data.filter(item => getTestConfigKey(item.scannables)
            .toLowerCase()
            .includes(scannablesSearchInput.toLowerCase()))
            .filter(item => item.destination
                .toLowerCase()
                .includes(destinationSearchInput.toLowerCase())
            )
        data = data.sort((a, b)=>
            sortTestConfigColumns[testConfigSortColumn](a, b, testConfigSortOrder === ASCENDING)
        )
        return data;
    }

    const getDataForStowConfigTable = () => {
        return getDraftData({
            stateKey: CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION,
            subKey: selectedMac
        }).sort((a, b) => {
            return sortStowConfigColumns[stowConfigSortColumn](a, b, stowConfigSortOrder === ASCENDING);
        });
    }

    const getSortedDeviceList = () => {
        // Device List sorted based on device "name"
        const sortedDeviceList = Object.values(viewModel.tesseractDeviceInformation);
        sortedDeviceList && sortedDeviceList.sort((a, b) => {
            if (a.name < b.name) {
                return -1
            } else if (b.name > a.name) {
                return 1
            }
            return 0
        })
        return sortedDeviceList
    }

    const sortStowConfigColumns = {
        containerType: (a, b, ascending) => {
            return ascending ? sortComparator(a.containerType, b.containerType) : -sortComparator(a.containerType, b.containerType);
        },
        containerLabel: (a, b, ascending) => {
            return ascending ? sortComparator(a.containerLabel, b.containerLabel) : -sortComparator(a.containerLabel, b.containerLabel);
        }
    }

    const sortTestConfigColumns = {
        scannables: (a, b, ascending) => {
            let scannableFirst = Array.isArray(a.scannables) ? a.scannables.join(' ') : a.scannables;
            let scannableSecond = Array.isArray(b.scannables) ? b.scannables.join(' ') : b.scannables;
            return ascending ? sortComparator(scannableFirst, scannableSecond) : -sortComparator(scannableFirst, scannableSecond);
        },
        destination: (a, b, ascending) => {
            return ascending ? sortComparator(a.destination, b.destination) : -sortComparator(a.destination, b.destination);
        }
    }

    const getHeaderProps = () => {
        return {
            selectedMac,
            setSelectedMac,
            valueConfiguration,
            setValueConfiguration,
            defaultSelection: "",
            resetDraftData,
            deployDraft: deployDraftTesseractAsset,
            scrollToBottom,
            sortedDeviceList: getSortedDeviceList(),
            configurationKeys: PAGE_CONFIGURATION_KEYS,
            refreshDeps: [viewModel.timestamp],
            isDeployDisabled: !isDataEdited(PAGE_CONFIGURATION_KEYS),
            isRollbackDisabled: !selectedMac || isDataEdited(PAGE_CONFIGURATION_KEYS),
            rollbackClickHandler: () => {getModalContent(); setShowRollbackModal(true)}
        }
    }

    function onClickAddSingle() {
        setOpenAddModal(true)
    }

    function onClickUploadCSV() {
        setOpenCSVModal(true)
    }

    function onClickDeleteConfiguration() {
        setOpenDeleteModal(true)
    }

    const renderTestConfigTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "table.index"})}
                </TableCell>
                <TableCell sortColumn="scannables">
                    {getLocalizedMessage({intl, stringId: "test.scannables"})}
                </TableCell>
                <TableCell sortColumn="destination">
                    {getLocalizedMessage({intl, stringId: "test.destination"})}
                </TableCell>
            </TableRow>
        )
    }

    const renderStowConfigTableHeader = () => {
        return (
            <TableRow highlightOnHover={false}>
                <TableCell>
                    {getLocalizedMessage({intl, stringId: "table.index"})}
                </TableCell>
                <TableCell sortColumn="containerType">
                    {getLocalizedMessage({intl, stringId: "container.type"})}
                </TableCell>
                <TableCell sortColumn="containerLabel">
                    {getLocalizedMessage({intl, stringId: "container.label"})}
                </TableCell>
            </TableRow>
        )
    }

    const renderSearchRow = () => {
        return (
            <TableRow backgroundColor={SEARCH_ROW_COLOR}>
                <TableCell/>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={scannablesSearchInput}
                        onChange={setScannablesSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                <TableCell>
                    <Input
                        className="input-search-class"
                        value={destinationSearchInput}
                        onChange={setDestinationSearchInput}
                        type="text"
                        placeholder="Search"
                        width="70%"
                        size="small"/>
                </TableCell>
                <TableCell/>
            </TableRow>
        )
    }

    const renderTestConfigTableRow = (config, serialNumber) => {
        const dataString = config.destination
        const prevDataString = chainWalk(() => config.prev.destination, null)
        return  (
            <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {serialNumber}
                </TableCell>
                <TableCell>
                    {Array.isArray(config.scannables) ? config.scannables.join(' ') : config.scannables}
                </TableCell>
                <TableCell>
                    {prevDataString && <Text> {prevDataString} </Text>}
                    {prevDataString && <Icon tokens={arrowRightTokens}/>}
                    <Text> {dataString} </Text>
                </TableCell>
            </TableRow>
        )
    }

    const renderStowConfigTableRow = (config, serialNumber) => {
        const dataString = config.containerLabel
        const prevDataString = chainWalk(() => config.prev.containerLabel, null)
        return  (
            <TableRow highlightOnHover={true} backgroundColor={OPERATION_COLOR[config.operation]}>
                <TableCell>
                    {serialNumber}
                </TableCell>
                <TableCell>
                    {config.containerType}
                </TableCell>
                <TableCell>
                    {prevDataString && <Text> {prevDataString} </Text>}
                    {prevDataString && <Icon tokens={arrowRightTokens}/>}
                    <Text> {dataString} </Text>
                </TableCell>
            </TableRow>
        )
    }

    if (viewModel.loadingOverview) {
        return (<Loader/>)
    } else {
        return (
            <Column>
                <ConfigurationTabHeader {...getHeaderProps()}/>
                <RollbackModal
                    showRollbackModal={showRollbackModal}
                    setShowRollbackModal={setShowRollbackModal}
                    isVersionsLoading={isVersionsLoading}
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}
                    rollBackVersions={rollBackVersions}
                    loadRollbackDataToDraft={loadRollbackDataToDraft}
                    intl={intl}
                />
                {valueConfiguration === CONFIG_TYPE.TESSERACT_STOW_CONFIGURATION && selectedMac &&
                    <Column>
                        <PaginatedTable
                            data={getDataForStowConfigTable()}
                            renderHeader={renderStowConfigTableHeader}
                            renderRow={renderStowConfigTableRow}
                            sortColumn={stowConfigSortColumn}
                            sortDirection={stowConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setStowConfigSortColumn(sortColumn);
                                setStowConfigSortOrder(sortDirection);
                            }}
                        />
                        <Row width="100%" widths={["100", "100", "fill", "100"]}>
                            <Button onClick={onClickAddSingle}
                                    type="primary"
                                    size="small">
                                <Icon tokens={plusCircleTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowconfig.addupdate"})}
                            </Button>
                            <Button onClick={onClickUploadCSV}
                                    type="primary"
                                    size="small">
                                <Icon tokens={uploadTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowconfig.uploadCSV"})}
                            </Button>
                            <Box></Box>
                            <Button onClick={onClickDeleteConfiguration}
                                    size="small" type="tertiary">
                                <Icon tokens={trashTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowconfig.deleteconfig"})}
                            </Button>
                        </Row>
                        <UploadFileModal mac={selectedMac}
                                         open={openCSVModal} setOpen={setOpenCSVModal}
                                         parser={TesseractStowConfigCSVParser} updateConfig={updateStowConfigDraftData}/>
                        <AddTesseractStowConfigurationModal mac={selectedMac}
                                                         open={openAddModal} setOpen={setOpenAddModal}/>
                        <DeleteTesseractStowConfigurationModal mac={selectedMac}
                                                             open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                    </Column>
                }
                {valueConfiguration === CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION && selectedMac &&
                    <Column>
                        <PaginatedTable
                            data={searchDataForTestConfigTable()}
                            renderHeader={renderTestConfigTableHeader}
                            renderSearchRow={renderSearchRow}
                            renderRow={renderTestConfigTableRow}
                            sortColumn={testConfigSortColumn}
                            sortDirection={testConfigSortOrder}
                            onSort={({sortColumn, sortDirection}) => {
                                setTestConfigSortColumn(sortColumn);
                                setTestConfigSortOrder(sortDirection);
                            }}
                        />
                        <Row width="100%" widths={["100", "100", "fill", "100"]}>
                            <Button onClick={onClickAddSingle}
                                    type="primary"
                                    size="small">
                                <Icon tokens={plusCircleTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowtestconfig.addupdate"})}
                            </Button>
                            <Button onClick={onClickUploadCSV}
                                    type="primary"
                                    size="small">
                                <Icon tokens={uploadTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowtestconfig.uploadCSV"})}
                            </Button>
                            <Box></Box>
                            <Button onClick={onClickDeleteConfiguration}
                                    size="small" type="tertiary">
                                <Icon tokens={trashTokens}/>
                                {getLocalizedMessage({intl, stringId: "stowtestconfig.deleteconfig"})}
                            </Button>
                        </Row>
                        <AddTesseractTestPackageModal mac={selectedMac}
                                                      open={openAddModal} setOpen={setOpenAddModal}/>
                        <UploadFileModal mac={selectedMac}
                                         open={openCSVModal} setOpen={setOpenCSVModal}
                                         parser={TesseractTestConfigCSVParser} updateConfig={updateTestConfigDraftData}/>
                        <DeleteTestPackageConfigurationModal mac={selectedMac}
                                                             testConfig={CONFIG_TYPE.TESSERACT_TEST_CONFIGURATION}
                                                             open={openDeleteModal} setOpen={setOpenDeleteModal}/>
                        <div ref={tableEndRef}/>
                    </Column>
                }
            </Column>
        )
    }
}

export default injectIntl(TesseractDeviceConfiguration);


// WEBPACK FOOTER //
// ./src/components/devicemanagement/tesseract/TesseractDeviceConfiguration.js